import { Injectable } from '@angular/core';
import { State, Action, StateContext, Selector, Store } from '@ngxs/store';
import { Menus } from './menus.action';
import { MenuItem } from 'src/app/layout/app.menu.items';
import { ProductsState } from '../products/products.state';
import { MENUS_STATE } from '..';

/**
 * @description
 * Defines the shape of the menu state in the NGXS store.
 */
export interface MenuStateModel {
  validDashboardPaths: string[];
}

@State<MenuStateModel>({
  name: MENUS_STATE,
  defaults: {
    validDashboardPaths: [],
  },
})
@Injectable()
export class MenuState {
  constructor(private store: Store) { }

  /**
   * A selector to get the array of valid dashboard paths from the state.
   * @param state The current menu state model.
   * @returns An array of strings representing the valid paths.
   */
  @Selector()
  static getValidDashboardIds(state: MenuStateModel): string[] {
    return state.validDashboardPaths;
  }

  /**
   * An action handler that populates the state with valid dashboard paths
   * by reading the menu structure from the ProductsState.
   * @param ctx The state context object.
   */
  @Action(Menus.LoadValidDashboardPaths)
  loadValidDashboardPaths(ctx: StateContext<MenuStateModel>) {
    const productMenuModel = this.store.selectSnapshot(ProductsState.selectedProductMenuModel);

    let idsArray: string[] = [];

    // Check if a product is selected and if it contains menu items.
    if (productMenuModel) {
      const idsSet = this.extractIdsRecursive(productMenuModel);
      idsArray = Array.from(idsSet);
    }

    ctx.patchState({
      validDashboardPaths: idsArray,
    });
  }

  /**
   * Recursively traverses the menu items to extract dashboard paths.
   * This logic remains the same.
   * @param items The array of MenuItems to process.
   * @returns A Set of unique dashboard paths.
   */
  private extractIdsRecursive(items: MenuItem[]): Set<string> {
    const ids = new Set<string>();
    for (const item of items) {
      // Locked/hidden branch is not a valid link
      // explicitly compared to 'false' because not all item declaring 'visible'
      if (item.visible === false || item.disabled) {
        continue;
      }

      if (item.routerLink && item.routerLink[0]?.startsWith('/dashboards/')) {
        const parts = item.routerLink[0].split('/');
        if (parts.length > 3) {
          ids.add(parts[3]);
        }
      }

      if (item.items && item.items.length > 0) {
        this.extractIdsRecursive(item.items).forEach((id) => ids.add(id));
      }
    }
    return ids;
  }
}

